import { useReferPropertyMutation } from "@/src-refactor/modules/store/qrtk/services/realestate/realestate.qrtk";
import toast from "react-hot-toast";
import MxModalTemplate2 from "@rf-l1/modal/MxModalTemplate2";
import useModal from "@rf/base/modal/useModal";

const usePropertyShareReferPropertyModal = () => {
  const { modal } = useModal();

  const handleOpenReferProperty = async (arg: ICardProperty[]) => {
    return modal({
      template: <MxModalTemplate2 children={<PropertyConflictCheckerTemplate />} />,
      data: arg,
      options: {
        title: "Propiedades a referir",
        disableCloseButton: false,
        size: {
          xs: {},
          sm: {},
          md: {},
          lg: {},
          xl: {
            width: "100%",
            maxWidth: "70vw",
            maxHeight: "550px"
          }
        }
      }
    });
  };
  return { handleOpenReferProperty };
};

export default usePropertyShareReferPropertyModal;

import { Grid, styled } from "@mui/material";
import MxButton2 from "@rf-l1/buttons/MxButton2";
import MxCardPropertySmall from "@rf-l1/cards/property/MxCardPropertySmall";
import MxTypography from "@rf-l1/MxTypography";
import { ICardProperty } from "@rf-model/card-property.model";
import MxFormL3ChipInput from "@rf-l3/form/MxFormL3ChipInput";
import { InferType, object } from "yup";
import Yup from "@rf/yup/yup-extensions";
import { HGetKeyEnums } from "@rf-helper/generat-enums-from-keys";
import MxFormL3TextAreaField from "@rf-l3/form/MxFormL3TextAreaField";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import MxFormSubTitle from "@rf-l1/page/form/MxFormSubTitle";
import { HToastCatchServerError } from "@rf-helper/cath-error-server";

const PropertyConflictCheckerTemplate = () => {
  const { getData, onClose, onConfirm } = useModal();

  const [REFERER_PROPERTY_M] = useReferPropertyMutation();
  const data = getData() as ICardProperty[];

  const form = useForm({
    defaultValues: CRefererProperty,
    values: {
      ...CRefererProperty,
      propertiesIds: data.map(x => x.id)
    },
    resolver: yupResolver(schemaRefererProperty)
  });

  // Watch emails field for real-time validation
  const emails = useWatch({
    control: form.control,
    name: CRefererPropertyKeys.emails,
    defaultValue: []
  });

  // Get invalid emails for display
  const invalidEmails = validateEmails(emails as string[]);
  const hasInvalidEmails = invalidEmails.length > 0;

  const onSubmit = (values: IRefererPropertySchema) => {
    // Test email validity before submitting
    const emails = values.emails.filter(x => x !== undefined) as string[];
    const validationMessage = testEmailValidation(emails);

    if (validationMessage !== "Todos los correos son válidos") {
      toast.error(validationMessage);
      return;
    }

    const request = REFERER_PROPERTY_M({
      body: {
        emails: emails,
        message: values.comment,
        referRealEstateId: values.propertiesIds,
        subject: "Referencia de propiedades"
      }
    }).unwrap();

    toast
      .promise(request, {
        loading: "Enviando referimiento",
        success: "Propiedades referidas con éxito",
        error: "Error al referir propiedades"
      })
      .then(() => {
        onConfirm();
      })
      .catch(e => HToastCatchServerError(e));
  };

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} md={12} lg={7}>
        <ContentProperties
          container
          justifyContent={data.length == 1 ? "center" : "flex-start"}
          gap={1}
        >
          {data.map((x, i: number) => (
            <Grid item flexGrow={1} key={i} maxWidth={300} minWidth={300}>
              <MxCardPropertySmall {...x} />
            </Grid>
          ))}
        </ContentProperties>
      </Grid>

      <Grid container item xs={12} md={12} lg={5}>
        <FormProvider {...form}>
          <Grid
            container
            item
            xs={12}
            alignContent="flex-start"
            component="form"
            onSubmit={form.handleSubmit(onSubmit)}
            rowGap={2}
          >
            <Grid item xs={12} pb={2}>
              <MxTypography variant="caption" fontWeight={400} color="grey.600">
                Haz seleccionado estas propiedades, completa los siguientes campos
                para referir a los correos electrónicos
              </MxTypography>
            </Grid>
            <Grid container rowGap={2}>
              <Grid item xs={12}>
                <MxFormL3ChipInput
                  name={CRefererPropertyKeys.emails}
                  withreacthookform={1}
                  label="Correos electrónicos"
                />
                {hasInvalidEmails && (
                  <MxTypography
                    variant="overline"
                    color="error.main"
                    mt={0.5}
                    ml={1.5}
                  >
                    ⚠️ Correos inválidos: {invalidEmails.join(", ")}
                  </MxTypography>
                )}
              </Grid>
              <Grid item xs={12}>
                <MxFormL3TextAreaField
                  name={CRefererPropertyKeys.comment}
                  withreacthookform={1}
                  minRows={3}
                  label="Comentarios"
                />
              </Grid>
            </Grid>

            <Grid
              container
              item
              xs={12}
              columnGap={1}
              justifyContent={"flex-end"}
              alignContent="flex-end"
            >
              <Grid container item xs={12} justifyContent="flex-end" my={1.4}>
                <MxTypography variant="caption" fontWeight={400} color="grey.600">
                  ¿Desea continuar?
                </MxTypography>
              </Grid>
              <Grid item xs="auto">
                <MxButton2
                  testId="cancel-refer-property-button"
                  bgColor={"grey.200"}
                  textColor={"grey.600"}
                  onClick={onClose}
                  sx={{
                    minWidth: 100
                  }}
                >
                  Cancelar
                </MxButton2>
              </Grid>
              <Grid item xs="auto">
                <MxButton2
                  testId="refer-property-button"
                  bgColor="primary.main"
                  variant="contained"
                  type="submit"
                  sx={{
                    minWidth: 100
                  }}
                >
                  Referir
                </MxButton2>
              </Grid>
            </Grid>
          </Grid>
        </FormProvider>
      </Grid>
    </Grid>
  );
};

const ContentProperties = styled(Grid)(({ theme }) => ({
  minHeight: 480,
  maxHeight: 480,
  background: "var(--m-color-gray-01)",
  overflow: "hidden",
  overflowY: "auto",
  padding: "0.5rem"
}));

const schemaRefererProperty = object({
  emails: Yup.array()
    .min(1, "Debe agregar al menos un correo")
    .test({
      name: "is-valid-email",
      message(params) {
        return testEmailValidation(Array.isArray(params.value) ? params.value : []);
      },
      test(value) {
        if (!value || !Array.isArray(value) || value.length === 0) return false;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
        return value.every(
          email => typeof email === "string" && emailRegex.test(email)
        );
      }
    })
    .label("Correos")
    .default([]),
  comment: Yup.string().default("").label("Comentario"),
  propertiesIds: Yup.array().label("Propiedades").default([])
});

// Function to test email validity
const validateEmails = (emails: string[]): string[] => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  return emails.filter(email => !emailRegex.test(email));
};

// Test function that returns the validation message
const testEmailValidation = (emails: string[]): string => {
  const invalidEmails = validateEmails(emails);
  if (invalidEmails.length === 0) {
    return "Todos los correos son válidos";
  }
  if (invalidEmails.length == 1) {
    return `${invalidEmails[0]} no es un correo válido`;
  }
  return `${invalidEmails.join(", ")} no son correos válidos`;
};

// Usage example:
// const message = testEmailValidation(["tomates", "tomates@gmail.com", "t.com"]);
// Results: "tomates, t.com no son correos válidos"

type IRefererPropertySchema = InferType<typeof schemaRefererProperty>;
const CRefererProperty: IRefererPropertySchema = schemaRefererProperty.getDefault();
const CRefererPropertyKeys = HGetKeyEnums(CRefererProperty) as {
  [K in keyof typeof CRefererProperty]: K;
};
